import React from 'react';
import PropTypes from 'prop-types';
import { graphql, compose } from 'react-apollo';
import withStyles from 'isomorphic-style-loader/lib/withStyles';
import { FormattedMessage, injectIntl } from 'react-intl';
import { FaUser } from 'react-icons/fa';
import cx from 'classnames';
import { Dropdown } from 'react-bootstrap';
import moment from 'moment';

import history from '../../../core/history';
import messages from '../../../locale/messages';
import { USER_SIGNUP_NOTIFICATION_TYPE } from '../../../helpers/adminNotificationTypes';
import { markAdminNotificationsReadOnOpen } from '../../../helpers/adminNotificationMarkReadOnOpen';

import UnreadCountQuery from '../AdminNotificationDropdown/getAdminNotificationUnreadCount.graphql';
import NotificationsQuery from '../AdminNotificationDropdown/getAdminNotifications.graphql';
import MarkReadMutation from '../AdminNotificationDropdown/markAdminNotificationsRead.graphql';

import s from '../AdminNotificationDropdown/AdminNotificationDropdown.css';

// New signup admin notifications. AdminNotifications type: user_signup.
class AdminSignupNotificationDropdown extends React.Component {
  static propTypes = {
    unreadCountData: PropTypes.object,
    notificationsData: PropTypes.object,
    markAdminNotificationsRead: PropTypes.func,
  };

  constructor(props) {
    super(props);
    this.state = { isOpen: false, seeAllLimit: 10 };
    this.handleToggle = this.handleToggle.bind(this);
    this.handleNotificationClick = this.handleNotificationClick.bind(this);
    this.handleSeeAll = this.handleSeeAll.bind(this);
  }

  async handleToggle(isOpen) {
    this.setState({ isOpen });

    if (isOpen) {
      const { unreadCountData, notificationsData, markAdminNotificationsRead } = this.props;
      try {
        // Mark-read + refetch scoped to user_signup (user icon only).
        const { seeAllLimit } = await markAdminNotificationsReadOnOpen({
          notificationType: USER_SIGNUP_NOTIFICATION_TYPE,
          unreadCountData,
          notificationsData,
          markAdminNotificationsRead,
        });
        this.setState({ seeAllLimit });
      } catch (error) {
        console.error('Failed to mark signup notifications as read:', error);
      }
    }
  }

  handleNotificationClick(notification) {
    if (!notification?.profileId) {
      return;
    }
    this.setState({ isOpen: false });
    history.push({
      pathname: `/siteadmin/profileView/${notification.profileId}`,
      state: { userId: notification.userId },
    });
  }

  handleSeeAll() {
    const { seeAllLimit } = this.state;
    this.setState({ isOpen: false });
    // Open Manage Users with newest filter sized to unread count (rounded up to 10).
    history.push(`/siteadmin/users?recentLimit=${seeAllLimit}`);
  }

  render() {
    const { unreadCountData, notificationsData } = this.props;
    const { isOpen } = this.state;

    const loading = unreadCountData?.loading;
    const total = unreadCountData?.getAdminNotificationUnreadCount?.total || 0;
    const hasUnread = !loading && total > 0;

    const notifications = notificationsData?.getAdminNotifications || [];
    const notificationsLoading = notificationsData?.loading;

    return (
      <div className={s.notificationWrapper}>
        <Dropdown
          id="admin-signup-notifications-dropdown"
          open={isOpen}
          onToggle={this.handleToggle}
          pullRight
        >
          <Dropdown.Toggle noCaret className={s.notificationButton}>
            <FaUser className={s.notificationIcon} />
            {hasUnread && (
              <span className={s.notificationBadge}>{total}</span>
            )}
          </Dropdown.Toggle>
          <Dropdown.Menu className={s.dropdownMenu}>
            <div className={s.dropdownHeader}>
              <FormattedMessage {...messages.adminSignupNotificationsTitle} />
            </div>
            {notificationsLoading && (
              <div className={s.emptyMessage}>
                <FormattedMessage {...messages.adminNotificationsLoading} />
              </div>
            )}
            {!notificationsLoading && notifications.length === 0 && (
              <div className={s.emptyMessage}>
                <FormattedMessage {...messages.adminSignupNotificationsEmpty} />
              </div>
            )}
            {!notificationsLoading && notifications.length > 0 && (
              <ul className={s.notificationList}>
                {notifications.map((notification) => (
                  <li
                    key={notification.id}
                    className={cx(s.notificationItem, {
                      [s.notificationItemUnread]: !notification.isRead,
                    })}
                    onClick={() => this.handleNotificationClick(notification)}
                  >
                    <div className={s.notificationContent}>
                      <p className={s.notificationMessage}>
                        <span className={s.notificationUserName}>
                          {notification.userName}
                        </span>
                        {/* JSX collapses whitespace between inline siblings */}
                        {' '}
                        <FormattedMessage
                          {...messages.adminNotificationUserSignupSuffix}
                        />
                      </p>
                      <span className={s.notificationTime}>
                        {notification.createdAt
                          ? moment(notification.createdAt).fromNow()
                          : ''}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
            <div className={s.dropdownFooter}>
              <span className={s.seeAllLink} onClick={this.handleSeeAll}>
                <FormattedMessage {...messages.adminNotificationsSeeAll} />
              </span>
            </div>
          </Dropdown.Menu>
        </Dropdown>
      </div>
    );
  }
}

export default compose(
  injectIntl,
  withStyles(s),
  graphql(UnreadCountQuery, {
    name: 'unreadCountData',
    options: {
      ssr: false,
      pollInterval: 5000,
      fetchPolicy: 'network-only',
      variables: { type: USER_SIGNUP_NOTIFICATION_TYPE },
    },
  }),
  graphql(NotificationsQuery, {
    name: 'notificationsData',
    options: {
      ssr: false,
      fetchPolicy: 'network-only',
      variables: { type: USER_SIGNUP_NOTIFICATION_TYPE, limit: 10 },
    },
  }),
  graphql(MarkReadMutation, {
    name: 'markAdminNotificationsRead',
  }),
)(AdminSignupNotificationDropdown);
